import { useGetOrganizationQuery } from "modules/auth/hooks/useGetOrganizationQuery"
import { useIntegrationsStatusQuery } from "modules/settings/hooks/useIntegrationsStatusQuery"
import { Loading } from "modules/shared/components/Loading"
import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  CapabilitiesResponseDataSchema,
  CapabilityCampaignAssignation,
  CapabilityResponseDataSchema,
  CompanyIntegrationProviderEnum,
} from "schema"
import { toast } from "sonner"
import z from "zod"
import { getProviderOptionKey, isProviderEnabled } from "../../lib/providerUtils"
import { useInstallCapabilityMutation } from "../../hooks/useInstallCapabilityMutation"
import { useUpdateCapabilityConfigMutation } from "../../hooks/useUpdateCapabilityConfigMutation"
import { CapabilityOrganizationPhoneNumbers } from "./CapabilityOrganizationPhoneNumbers"

type Provider = z.infer<typeof CompanyIntegrationProviderEnum>

type Props = {
  capability: z.infer<typeof CapabilitiesResponseDataSchema>[number]
  mode?: "install" | "update"
  initialConfig?: { provider?: string; organizationPhoneNumbers?: string | null }
  onClose: () => void
  onPendingChange?: (isPending: boolean) => void
  onConfigure?: (capability: z.infer<typeof CapabilityResponseDataSchema>) => void
  onSubmitHandlerRef?: React.MutableRefObject<
    | ((
        campaignAssignation: CapabilityCampaignAssignation,
        campaignIds: string[],
        campaignFlow?: "Inbound" | "Outbound",
      ) => Promise<void>)
    | null
  >
}

export function GetOrderRefundStatusForm({
  capability,
  mode = "install",
  initialConfig,
  onClose,
  onPendingChange,
  onConfigure,
  onSubmitHandlerRef,
}: Props) {
  const { t } = useTranslation()
  const { mutateAsync: installCapability, isPending: isInstallPending } =
    useInstallCapabilityMutation()
  const { mutateAsync: updateCapabilityConfig, isPending: isUpdatePending } =
    useUpdateCapabilityConfigMutation()
  const [organizationPhoneNumbers, setOrganizationPhoneNumbers] = useState(
    initialConfig?.organizationPhoneNumbers ?? "",
  )
  const { data: integrationsStatus, isPending: isIntegrationsStatusPending } =
    useIntegrationsStatusQuery()
  const { data: organizationData, isPending: isOrganizationPending } = useGetOrganizationQuery()

  const isPending =
    (mode === "install" ? isInstallPending : isUpdatePending) ||
    isIntegrationsStatusPending ||
    isOrganizationPending
  const activeProviders = (capability.supportedProviders ?? []).filter(
    (provider) =>
      provider !== "Shopify" && isProviderEnabled(provider as Provider, integrationsStatus),
  )
  const isShopifyEnabled = isProviderEnabled("Shopify", integrationsStatus)

  useEffect(() => {
    onPendingChange?.(isPending)
  }, [isPending, onPendingChange])

  useEffect(() => {
    if (!isOrganizationPending && organizationData) {
      setOrganizationPhoneNumbers(organizationData.phoneNumbers ?? "")
    }
  }, [organizationData, isOrganizationPending])

  const handleSubmit = useCallback(
    async (
      campaignAssignation: CapabilityCampaignAssignation,
      campaignIds: string[],
      campaignFlow?: "Inbound" | "Outbound",
    ) => {
      if (isPending) return

      let installedCapability: z.infer<typeof CapabilityResponseDataSchema> | undefined

      if (mode === "install") {
        installedCapability = await installCapability({
          identifier: capability.identifier,
          config: { organizationPhoneNumbers },
          campaignAssignation,
          campaignIds,
          campaignFlow,
        })

        toast.success(
          t("capabilities.list.install.success", {
            capability: t(`capabilities.catalog.${capability.identifier}.name`),
          }),
        )
      } else {
        await updateCapabilityConfig({
          id: capability.id,
          config: { organizationPhoneNumbers },
        })
      }

      if (onConfigure && installedCapability) {
        onConfigure(installedCapability)
      } else {
        onClose()
      }
    },
    [
      isPending,
      mode,
      onConfigure,
      installCapability,
      capability.identifier,
      capability.id,
      t,
      updateCapabilityConfig,
      onClose,
      organizationPhoneNumbers,
    ],
  )

  if (onSubmitHandlerRef) {
    onSubmitHandlerRef.current = handleSubmit
  }

  if (isIntegrationsStatusPending || isOrganizationPending) return <Loading />

  return (
    <div className="flex flex-col gap-4">
      <p className="text-muted-foreground">
        {t(`capabilities.catalog.${capability.identifier}.config.description`)}
      </p>

      <div className="flex flex-col gap-1.5">
        <p className="font-medium">
          {t(`capabilities.catalog.${capability.identifier}.config.activeProviders`)}
        </p>
        {activeProviders.length > 0 ? (
          <ul className="list-disc pl-5 text-muted-foreground">
            {activeProviders.map((provider) => {
              const optionKey = getProviderOptionKey(provider as Provider)

              return (
                <li key={provider}>
                  {t(
                    `capabilities.catalog.${capability.identifier}.config.providers.${optionKey}`,
                    { defaultValue: provider },
                  )}
                </li>
              )
            })}
          </ul>
        ) : (
          <p className="text-muted-foreground">
            {t(`capabilities.catalog.${capability.identifier}.config.noActiveProviders`)}
          </p>
        )}
      </div>

      {isShopifyEnabled && (
        <div className="flex flex-col gap-1.5">
          <p className="font-medium">
            {t(`capabilities.catalog.${capability.identifier}.config.shopifyFallback`)}
          </p>
          <p className="text-muted-foreground">
            {t(`capabilities.catalog.${capability.identifier}.config.shopifyFallbackDescription`)}
          </p>
        </div>
      )}

      <CapabilityOrganizationPhoneNumbers
        organizationPhoneNumbers={organizationPhoneNumbers}
        setOrganizationPhoneNumbers={setOrganizationPhoneNumbers}
      />

      <p className="text-muted-foreground">
        {t(`capabilities.catalog.${capability.identifier}.config.flow`)}
      </p>
    </div>
  )
}
